import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';

const ADMIN_EMAIL = 'pgundepudi@gmail.com';

/**
 * Common App Header - Used across all authenticated pages
 * Brand colors: Amber/Yellow (#F59E0B) as primary accent
 */
const AppHeader = ({ title, showCredits = true }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { workspace } = useWorkspace();
  
  const isAdmin = user?.email === ADMIN_EMAIL;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  return (
    <header className="px-6 py-3 bg-white border-b border-zinc-200">
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        {/* Left side - Page Title */}
        <div>
          <h1 className="text-base font-bold text-zinc-900" style={{fontFamily: 'Outfit'}}>
            {title || workspace?.name || 'Dashboard'}
          </h1>
          <p className="text-xs text-zinc-500">{user?.email}</p>
        </div>
        
        {/* Right side - Actions */}
        <div className="flex items-center gap-3">
          {/* Credits badge - hide for admin */}
          {showCredits && !isAdmin && (
            <div 
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => navigate('/credits')}
              title="View Credits"
            >
              <CreditCard className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">{workspace?.credits || 0}</span>
            </div>
          )}
          
          {/* Logout */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-zinc-200 text-zinc-600 hover:bg-zinc-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
